import type { CSSProperties, ReactNode } from 'react';
import { Card } from '@mantine/core';
import { TOKENS } from '../theme';
import { MonoLabel } from './MonoLabel';
import { staggerStyle } from './motion';

/**
 * The frame every chart and breakdown sits in: a title (Inter 600) with an
 * optional mono subtitle, a slot on the right for a legend or control, then
 * the body.
 *
 * `empty` swaps the body for a quiet message at the same height, so a period
 * with nothing in it keeps the page's shape instead of collapsing a card.
 */
export function ChartCard({
  title,
  subtitle,
  right,
  children,
  empty = false,
  emptyText = 'Nothing in this period',
  bodyHeight,
  flush = false,
  index = 0,
  style,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  empty?: boolean;
  emptyText?: ReactNode;
  /** Fixes the body height, so loading, empty and loaded states all match. */
  bodyHeight?: number;
  /** Body runs edge to edge, for a table that brings its own cell padding. */
  flush?: boolean;
  index?: number;
  style?: CSSProperties;
}) {
  return (
    <Card
      className="hf-rise hf-chart"
      h="100%"
      padding={0}
      style={staggerStyle(index, { display: 'flex', flexDirection: 'column', ...style })}
    >
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: 16, padding: '18px 20px 0', flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: TOKENS.text, letterSpacing: '-0.005em' }}>
            {title}
          </div>
          {subtitle && <MonoLabel style={{ marginTop: 3 }}>{subtitle}</MonoLabel>}
        </div>
        {right && <div style={{ flex: 'none', maxWidth: '100%', overflowX: 'auto' }}>{right}</div>}
      </div>
      <div style={{ padding: flush ? '12px 0 6px' : '16px 20px 18px', flex: 1, minHeight: bodyHeight }}>
        {empty ? <EmptyBody height={bodyHeight}>{emptyText}</EmptyBody> : children}
      </div>
    </Card>
  );
}

function EmptyBody({ children, height }: { children: ReactNode; height?: number }) {
  return (
    <div
      style={{
        height: height ?? 160,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px dashed ${TOKENS.borderStrong}`,
        borderRadius: 10,
      }}
    >
      <MonoLabel>{children}</MonoLabel>
    </div>
  );
}

/** A legend entry: swatch, name and, optionally, a figure. */
export function LegendItem({
  color, label, value, line = false,
}: {
  color: string;
  label: ReactNode;
  value?: ReactNode;
  /** Draw the swatch as a short line (for a line series) instead of a square. */
  line?: boolean;
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: TOKENS.textSecondary }}>
      <span
        aria-hidden
        style={
          line
            ? { width: 12, height: 2, borderRadius: 2, background: color }
            : { width: 8, height: 8, borderRadius: 2, background: color }
        }
      />
      {label}
      {value !== undefined && (
        <span style={{ fontFamily: 'var(--mantine-font-family-monospace)', fontVariantNumeric: 'tabular-nums', color: TOKENS.text, fontWeight: 500 }}>
          {value}
        </span>
      )}
    </span>
  );
}
